import { CommonModule } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { AfterViewInit, Component, inject, OnChanges, Input as RouterInput, signal, WritableSignal } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { FileService } from '../../file/file.service';
import { BaseComponent } from '../../shared/base.component';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { FirmwareFile } from '../shared/firmware-file.model';
import { CustomPaginatorIntl } from './custom-paginator';

@Component({
    selector: 'app-view-firmware',
    standalone: true,
    imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatExpansionModule,
    MatTableModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    SiteHeaderComponent,
],
    providers: [{ provide: MatPaginatorIntl, useClass: CustomPaginatorIntl }],
    templateUrl: './view-firmware.component.html',
    styleUrl: './view-firmware.component.css'
})
export class ViewFirmwareComponent extends BaseComponent implements AfterViewInit, OnChanges {

    @RouterInput() vehicle: string = "";
    @RouterInput() model: string = "";

    private fileService: FileService = inject(FileService);

    private static _retrievingFirmwareFiles = "Retrieving Firmware Files";
    private static _retrievingFirmwareFilesError = `${ViewFirmwareComponent._retrievingFirmwareFiles} error:`;
    private static _downloadingFirmwareFile = "Downloading Firmware File";
    private static _downloadingFirmwareFileError = `${ViewFirmwareComponent._downloadingFirmwareFile} error:`;

    private _firmwareFiles: WritableSignal<FirmwareFile[]> = signal<FirmwareFile[]>([]);
    private _firmwareFileCount: WritableSignal<number> = signal<number>(-1);

    private _endingFirmwareFiles: string[] = [];

    private _lastAction: number = 0;

    public ngAfterViewInit() {
        this.listFirmwareFiles();
    }

    public ngOnChanges() {
        this.listFirmwareFiles();
    }

    protected get firmwareFiles(): FirmwareFile[] {
        return this._firmwareFiles();
    }

    private set firmwareFiles(value: FirmwareFile[]) {
        this._firmwareFiles.set(value);
    }

    protected get firmwareFileCount(): number {
        return this._firmwareFileCount();
    }

    protected override handlePageEvent(e: PageEvent): void {
        this._lastAction = this.pageIndex > e.pageIndex ? -1 : 1;
        const pageSize = this.pageSize;
        super.handlePageEvent(e);

        // If pageSize has changed, then reset pageIndex to page,
        // 1. This differs from other pages where the data comes
        // from the database and where 'offset' is supported in the
        // database query, and thus we can use the database offset
        // capability to refresh the current page with the new page
        // size.
        if (pageSize != this.pageSize) {
            this.pageIndex = 0;
        }

        this.listFirmwareFiles();
    }

    protected async listFirmwareFiles(): Promise<void>;
    protected async listFirmwareFiles(overrideSnackBar: string | undefined): Promise<void>;
    protected async listFirmwareFiles(overrideSnackBar?: string): Promise<void> {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            let endingFirmwareFileName: string = "";

            if (this.pageIndex > 0) {
                if (this.isLastActionPageForward) {
                    endingFirmwareFileName = this.firmwareFiles[this.firmwareFiles.length - 1].name;
                } else if (this.isLastActionPageBackward) {
                    endingFirmwareFileName = this._endingFirmwareFiles[this._endingFirmwareFiles.length - 2];
                } else {
                    // Last action == Delete.
                    endingFirmwareFileName = this._endingFirmwareFiles[this._endingFirmwareFiles.length - 1];
                }

            } else {
                this._endingFirmwareFiles = [];
            }

            const observable = this.fileService.getFiles(await getIdToken(currentUser),
                    "firmware", {"vehicle-type": this.vehicle, "vehicle-model": this.model},
                    this.pageSize, this.pageIndex, endingFirmwareFileName)

            if (overrideSnackBar == undefined) {
                super.showSnackBar(`${ViewFirmwareComponent._retrievingFirmwareFiles}...`, -1)
            }

            super.message = "";

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-firmware"] == "success") {
                        if (this.isLastActionPageForward) {
                            if (this.pageIndex > 0) {
                                this._endingFirmwareFiles.push(endingFirmwareFileName);
                            }
                        } else if (this.isLastActionPageBackward) {
                            this._endingFirmwareFiles.pop();
                        } else {
                            // Last Action == Delete.
                            // Do nothing with endingFirmwareFiles.
                        }

                        this.firmwareFiles = FirmwareFile.toFirmwareFiles(json["files"]);
                        this.firmwareFileCount = (this.pageIndex * this.pageSize) + json["file-count"];

                        // If there are more firmware files in the backend storage bucket, then increase
                        // firmwareFilecount by an additional pageSize + 1. We need to increase the
                        // firmwareFileCount to enable the paginator Next Page button.
                        // 
                        // Technically, we only need to increase the firmwareFileCount by 1 for purposes
                        // of enabling the Next Page button. However, the reason we increase firmwareFileCount
                        // by pageSize + 1 is to support firmwareic in the custom-paginator.ts file.
                        //
                        // If we only increased the firmwareFileCount by 1, then in the customer-paginator.ts
                        // we would not be able to differentiate between additional firmware files available
                        // in the backend storage bucket versus the last page of firmware files having only
                        // one firmware file.
                        //
                        // The firmwareFileCount here is the 'length' parameter in the custom-paginator.ts file.
                        if (json["is-more"]) {
                            this.firmwareFileCount = this.firmwareFileCount + this.pageSize + 1;
                        }
                        overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar);
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar, 30);
                    const message = `${ViewFirmwareComponent._retrievingFirmwareFilesError} ${(<Error>error).message}`;
                    console.error(message);
                    super.message = message;
                }
            });
        } else {
            const message = `${ViewFirmwareComponent._retrievingFirmwareFilesError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async downloadFile(fileNameLong: string, fileNameShort: string) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = this.fileService.getDownloadUrl(await getIdToken(currentUser), "firmware", fileNameLong);
            super.showSnackBar(`${ViewFirmwareComponent._downloadingFirmwareFile}...`, -1);

            try {
                observable.subscribe(response => {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-firmware-download-url"] == "success") {
                        this.initiateDownload(json.url, fileNameShort);
                    } else {
                        throw new Error(json["message"]);
                    }
                });
            } catch (error) {
                const message = `${ViewFirmwareComponent._downloadingFirmwareFileError} ${(<Error>error).message}`;
                console.error(message);
                super.showSnackBar(message, 30);
           }
        } else {
            const message = `${ViewFirmwareComponent._downloadingFirmwareFileError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    private async initiateDownload(url: string, fileName: string) {
        const observable = this.fileService.downloadFile(url);
        const downloadSub = observable.subscribe(event => {
            try {
                switch (event.type) {
                    case HttpEventType.Sent:
                        break;
                    case HttpEventType.ResponseHeader:
                        break;
                    case HttpEventType.DownloadProgress:
                        /*
                        let percentDone = 0;
                        if (event.total != undefined) {
                            percentDone = Math.round(100 * event.loaded / event.total);
                        }
                        console.firmware(`Download progress: ${percentDone}%`);
                        */
                        break;
                    case HttpEventType.Response:
                        if (event.status == 200) {
                            const blob = new Blob([event.body as BlobPart], { type: 'application/octet-stream' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = fileName; 
                            link.click();
                            link.remove();
                            this.showSnackBar(`Download completed.`);
                        } else {
                            throw new Error(`Download failed, status: ${event.status}, statusText: ${event.statusText}`)
                        }
                        break;
                }
            } catch (error) {
                const message = `${ViewFirmwareComponent._downloadingFirmwareFileError} ${(<Error>error).message}`;
                console.error(message);
                super.showSnackBar(message, 30);
            }
        });
    }

    private set firmwareFileCount(value: number) {
        this._firmwareFileCount.set(value);
    }

    private get isLastActionPageBackward(): boolean {
        return this._lastAction == -1;
    }

    private get isLastActionPageForward(): boolean {
        return this._lastAction == 1;
    }
}
